import { ConditionOperatorEnumType } from '@reach/shared-types/conditions';
import { generateId } from '@/lib/generate-id';
import { ChevronDown, ChevronRight, Trash2 } from 'lucide-react';
import React from 'react';
import { AddDescriptionButton } from './AddDescriptionButton';
import { AddRelatedResourceCondition } from './AddRelatedResourceCondition';
import { ClauseDescriptionHeadline } from './ClauseDescriptionHeadline';
import { FieldConditionRow } from './FieldConditionRow';
import { RelatedResourceBlock } from './RelatedResourceBlock';
import { useSegmentSchemaCatalog } from './useSegmentSchemaCatalog';
import type {
  BuilderCondition,
  BuilderFieldCondition,
  BuilderNestedGroup,
  BuilderRelatedResourceCondition,
} from './segment-builder.types';
import {
  isBuilderFieldCondition,
  isBuilderNestedGroup,
  isBuilderRelatedResourceCondition,
} from './segment-builder.types';
import styles from './SegmentVisualBuilder.module.css';

interface NestedGroupBlockProps {
  condition: BuilderNestedGroup;
  onUpdate: (condition: BuilderNestedGroup) => void;
  onDelete: () => void;
  disabled?: boolean;
  depth?: number;
}

/**
 * Recursive component for rendering nested condition groups.
 * Supports arbitrary nesting with field conditions, related resources, and other nested groups.
 */
export const NestedGroupBlock: React.FC<NestedGroupBlockProps> = ({
  condition,
  onUpdate,
  onDelete,
  disabled = false,
  depth = 0,
}) => {
  const { schemaCatalog } = useSegmentSchemaCatalog();
  if (!schemaCatalog) return null;

  const { contactFields } = schemaCatalog;
  const isExpanded = condition.isExpanded;

  const toggleExpanded = () => {
    onUpdate({ ...condition, isExpanded: !condition.isExpanded });
  };

  const handleLogicChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const logic = e.target.value === 'or' ? 'or' : 'and';
    onUpdate({ ...condition, logic });
  };

  const handleDescriptionChange = (description: string | undefined) => {
    onUpdate({ ...condition, description });
  };

  const handleConditionUpdate = (index: number, cond: BuilderCondition) => {
    onUpdate({ ...condition, conditions: condition.conditions.with(index, cond) });
  };

  const handleConditionDelete = (index: number) => {
    onUpdate({ ...condition, conditions: condition.conditions.toSpliced(index, 1) });
  };

  const handleAddFieldCondition = () => {
    const firstField = contactFields[0];
    const newCond: BuilderFieldCondition = {
      id: generateId(),
      type: 'field',
      field: firstField?.name ?? '',
      fieldType: firstField?.type ?? 'unknown',
      operator: ConditionOperatorEnumType.EQUALS,
      value: [],
    };
    onUpdate({
      ...condition,
      conditions: [...condition.conditions, newCond],
    });
  };

  const handleAddNestedGroup = () => {
    // New nested group with opposite logic for clarity
    const newNestedGroup: BuilderNestedGroup = {
      id: generateId(),
      type: 'nested_group',
      logic: condition.logic === 'and' ? 'or' : 'and',
      conditions: [],
      isExpanded: true,
    };
    onUpdate({
      ...condition,
      conditions: [...condition.conditions, newNestedGroup],
    });
  };

  const handleAddRelatedResource = (newCond: BuilderRelatedResourceCondition) => {
    onUpdate({
      ...condition,
      conditions: [...condition.conditions, newCond],
    });
  };

  return (
    <div className={styles.nestedGroupBlock}>
      <div className={styles.nestedGroupHeader}>
        <div className={styles.nestedGroupTitle}>
          <button
            type="button"
            className={styles.relatedTitleButton}
            onClick={toggleExpanded}
            disabled={disabled}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? 'Collapse' : 'Expand'}>
            {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
          </button>
          <span>Nested Group</span>
          <select
            value={condition.logic}
            onChange={handleLogicChange}
            disabled={disabled}
            aria-label="Nested group logic">
            <option value="and">AND</option>
            <option value="or">OR</option>
          </select>
        </div>
        <div className={styles.nodeHeaderControls}>
          {condition.description === undefined && (
            <AddDescriptionButton onClick={() => handleDescriptionChange('')} disabled={disabled} />
          )}
          <button
            type="button"
            className={styles.relatedTitleButton}
            onClick={onDelete}
            disabled={disabled}
            aria-label="Remove nested group">
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      {condition.description !== undefined && (
        <div className={styles.nestedGroupDescription}>
          <ClauseDescriptionHeadline
            variant="group"
            description={condition.description}
            onChange={handleDescriptionChange}
            disabled={disabled}
          />
        </div>
      )}

      {isExpanded && (
        <div className={styles.nestedGroupContent}>
          {condition.conditions.map((cond, i) => (
            <React.Fragment key={cond.id}>
              {i > 0 && (
                <div className={styles.andConnector}>
                  <span className={styles.andConnectorLabel}>{condition.logic.toUpperCase()}</span>
                </div>
              )}
              {isBuilderFieldCondition(cond) ? (
                <div style={{ padding: '0 0.75rem' }}>
                  <FieldConditionRow
                    condition={cond}
                    fieldOptions={contactFields}
                    onUpdate={handleConditionUpdate.bind(null, i)}
                    onDelete={handleConditionDelete.bind(null, i)}
                    disabled={disabled}
                  />
                </div>
              ) : isBuilderRelatedResourceCondition(cond) ? (
                <RelatedResourceBlock
                  condition={cond}
                  onUpdate={handleConditionUpdate.bind(null, i)}
                  onDelete={handleConditionDelete.bind(null, i)}
                  disabled={disabled}
                />
              ) : isBuilderNestedGroup(cond) ? (
                <div style={{ padding: '0 0.75rem' }}>
                  <NestedGroupBlock
                    condition={cond}
                    onUpdate={handleConditionUpdate.bind(null, i)}
                    onDelete={handleConditionDelete.bind(null, i)}
                    disabled={disabled}
                    depth={depth + 1}
                  />
                </div>
              ) : null}
            </React.Fragment>
          ))}

          <div className={styles.nestedGroupAddRow}>
            <button
              type="button"
              className={styles.addConditionBtn}
              onClick={handleAddFieldCondition}
              disabled={disabled || contactFields.length === 0}>
              + Condition
            </button>
            <button
              type="button"
              className={styles.addConditionBtn}
              onClick={handleAddNestedGroup}
              disabled={disabled}>
              + Nested {condition.logic === 'and' ? 'OR' : 'AND'} group
            </button>
            <AddRelatedResourceCondition
              subjectSchemaId={schemaCatalog.contactSchemaId}
              addRelatedResourceCondition={handleAddRelatedResource}
              disabled={disabled}
            />
          </div>
        </div>
      )}
    </div>
  );
};
